// =====================================================================
//  ADMIN CONSOLE, ATTENDANCE
// ---------------------------------------------------------------------
//  The org-wide daily register plus the trend behind it.
//
//  The employee portal answers "was I here?". This answers "who is here,
//  who isn't, and who should I ask about it", one row per active person
//  for a chosen date, resolved server-side from punch data, the HR
//  register and approved leave, so absences and holidays aren't confused
//  with each other.
//
//  The Exceptions tab is the working view: it filters straight to the
//  people whose day needs a human to look at it.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  LogIn,
  Users,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import {
  Button,
  ChartTooltip,
  Chip,
  FilterChip,
  Grid,
  KpiTile,
  Notice,
  PageHeader,
  Panel,
  PanelBody,
  PanelHead,
  PersonCell,
  SearchInput,
  SegmentedControl,
  StatusChip,
  Tabs,
  Toolbar,
  ToolbarSpacer,
  toast,
  useDebounced,
} from '../ui';
import { series } from '../theme';
import {
  DASH,
  addDays,
  distinct,
  downloadCsv,
  fmtDate,
  fmtHours,
  fmtNumber,
  fmtPercent,
  fmtTime,
  matchesSearch,
  orDash,
  toISODate,
} from '../format';

const TREND_RANGES = [
  { key: 14, label: '14d' },
  { key: 30, label: '30d' },
  { key: 90, label: '90d' },
];

export default function AttendancePage({ navigate }) {
  const today = toISODate();
  const [date, setDate] = useState(today);
  const [tab, setTab] = useState('register');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [department, setDepartment] = useState('');
  const [trendDays, setTrendDays] = useState(30);

  const debouncedSearch = useDebounced(search, 250);

  const { data: register = [], isLoading } = useQuery({
    queryKey: ['admin', 'attendance-daily', date],
    queryFn: () => backendApi.admin.dailyAttendance(date),
  });

  const { data: snapshot } = useQuery({
    queryKey: ['admin', 'attendance-snapshot', date],
    queryFn: () => backendApi.admin.attendanceSnapshot(date),
  });

  const { data: trend = [] } = useQuery({
    queryKey: ['admin', 'attendance-trend', trendDays],
    queryFn: () => backendApi.admin.attendanceTrend(trendDays),
  });

  /* --------------------------- derived views -------------------------- */
  const exceptions = useMemo(
    () =>
      register.filter(
        (row) => row.status === 'Absent' || row.status === 'Late' || row.open_session
      ),
    [register]
  );

  const source = tab === 'exceptions' ? exceptions : register;

  const filtered = useMemo(
    () =>
      source.filter(
        (row) =>
          matchesSearch(row, debouncedSearch, ['name', 'code', 'email', 'department', 'position']) &&
          (!statusFilter || row.status === statusFilter) &&
          (!department || row.department === department)
      ),
    [source, debouncedSearch, statusFilter, department]
  );

  const departments = useMemo(() => distinct(register, 'department'), [register]);
  const workdayTrend = useMemo(() => trend.filter((d) => !d.isWeekend), [trend]);
  const isToday = date === today;
  const isFuture = date > today;

  /* ------------------------------ columns ----------------------------- */
  const columns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Employee',
        width: 240,
        render: (row) => <PersonCell name={row.name} email={row.email} sub={row.code || row.email} />,
      },
      { key: 'department', label: 'Department', width: 150, render: (row) => orDash(row.department) },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        render: (row) => (
          <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
            <StatusChip value={row.status} />
            {row.open_session ? (
              <Chip tone="info" title="Punched in without punching out">
                Open
              </Chip>
            ) : null}
          </span>
        ),
      },
      {
        key: 'check_in',
        label: 'Clock in',
        width: 110,
        render: (row) => (row.check_in ? fmtTime(row.check_in) : orDash(row.manual_check_in)),
        csv: (row) => (row.check_in ? fmtTime(row.check_in) : row.manual_check_in),
      },
      {
        key: 'check_out',
        label: 'Clock out',
        width: 110,
        render: (row) =>
          row.check_out ? (
            fmtTime(row.check_out)
          ) : row.open_session ? (
            <span style={{ color: 'var(--ac-info-fg)' }}>Still in</span>
          ) : (
            orDash(row.manual_check_out)
          ),
        csv: (row) => (row.check_out ? fmtTime(row.check_out) : row.manual_check_out),
      },
      {
        key: 'hours',
        label: 'Hours',
        width: 90,
        align: 'right',
        render: (row) => (row.hours ? fmtHours(row.hours) : DASH),
        csv: (row) => row.hours,
      },
      {
        key: 'leave_type',
        label: 'Leave',
        width: 120,
        render: (row) => (row.leave_type ? <Chip tone="info">{row.leave_type}</Chip> : DASH),
        csv: (row) => row.leave_type,
      },
      {
        key: 'source',
        label: 'Source',
        width: 90,
        defaultHidden: true,
        render: (row) => (row.source ? <Chip>{row.source}</Chip> : DASH),
      },
    ],
    []
  );

  const exportCsv = () => {
    downloadCsv(
      `attendance-${date}`,
      filtered,
      [
        { key: 'code', label: 'Employee ID' },
        { key: 'name', label: 'Name' },
        { key: 'department', label: 'Department' },
        { key: 'status', label: 'Status' },
        { key: 'check_in', label: 'Clock in', csv: (r) => (r.check_in ? fmtTime(r.check_in) : r.manual_check_in) },
        { key: 'check_out', label: 'Clock out', csv: (r) => (r.check_out ? fmtTime(r.check_out) : r.manual_check_out) },
        { key: 'hours', label: 'Hours' },
        { key: 'leave_type', label: 'Leave type' },
      ]
    );
    toast.success(`Exported ${filtered.length} rows`);
  };

  return (
    <div className="ac-page ac-page--wide">
      <PageHeader
        title="Attendance"
        sub="Who is in, who is out, and how the organization is trending."
        actions={
          <>
            <Button icon={Download} onClick={exportCsv} disabled={filtered.length === 0}>
              Export
            </Button>
            <Button icon={CalendarDays} onClick={() => setDate(today)} disabled={isToday}>
              Today
            </Button>
          </>
        }
      />

      {/* ---------------- DATE NAVIGATOR ---------------- */}
      <Panel style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', flexWrap: 'wrap' }}>
          <Button
            icon={ChevronLeft}
            size="sm"
            onClick={() => setDate(toISODate(addDays(date, -1)))}
            aria-label="Previous day"
          />
          <input
            type="date"
            className="ac-input"
            value={date}
            max={today}
            onChange={(event) => setDate(event.target.value || today)}
            style={{ width: 156 }}
            aria-label="Register date"
          />
          <Button
            icon={ChevronRight}
            size="sm"
            onClick={() => setDate(toISODate(addDays(date, 1)))}
            disabled={isToday}
            aria-label="Next day"
          />
          <strong style={{ fontSize: 13, color: 'var(--ac-ink)', marginLeft: 4 }}>
            {fmtDate(date)}
            {isToday ? <Chip tone="accent" style={{ marginLeft: 8 }}>Today</Chip> : null}
          </strong>
          <ToolbarSpacer />
          {snapshot ? (
            <span style={{ fontSize: 11.5, color: 'var(--ac-muted)' }}>
              {snapshot.present} of {snapshot.total} present · {fmtPercent(snapshot.presentRate)}
            </span>
          ) : null}
        </div>
      </Panel>

      {isFuture ? (
        <div style={{ marginBottom: 14 }}>
          <Notice tone="warning" title="Future date">
            There is no attendance data for a date that hasn't happened yet.
          </Notice>
        </div>
      ) : null}

      {/* ---------------- KPIs ---------------- */}
      {snapshot ? (
        <Grid cols={4} style={{ marginBottom: 14 }}>
          <KpiTile
            label="Present"
            value={fmtNumber(snapshot.present)}
            unit={`of ${snapshot.total}`}
            icon={Users}
            tone="pos"
            foot={fmtPercent(snapshot.presentRate)}
          />
          <KpiTile
            label="Absent"
            value={fmtNumber(snapshot.absent)}
            icon={AlertTriangle}
            tone={snapshot.absent > 0 ? 'neg' : 'pos'}
            foot={snapshot.onLeave > 0 ? `${snapshot.onLeave} on approved leave` : 'No approved leave today'}
          />
          <KpiTile
            label="Late arrivals"
            value={fmtNumber(snapshot.late)}
            icon={Clock}
            tone={snapshot.late > 0 ? 'warn' : 'pos'}
            foot="After 9:30 am"
          />
          <KpiTile
            label="Average hours"
            value={snapshot.averageHours || 0}
            unit="h"
            icon={LogIn}
            foot={snapshot.stillClockedIn > 0 ? `${snapshot.stillClockedIn} still clocked in` : 'All sessions closed'}
          />
        </Grid>
      ) : null}

      {/* ---------------- TREND ---------------- */}
      <Grid cols={2} style={{ marginBottom: 14 }}>
        <Panel>
          <PanelHead
            title="Attendance composition"
            sub="Working days only, weekends are excluded"
            actions={<SegmentedControl items={TREND_RANGES} value={trendDays} onChange={setTrendDays} />}
          />
          <PanelBody>
            <ResponsiveContainer width="100%" height={216}>
              <BarChart data={workdayTrend} margin={{ top: 6, right: 6, left: -20, bottom: 0 }} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={18} />
                <YAxis tickLine={false} axisLine={false} width={44} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(15,20,25,0.04)' }} />
                <Bar dataKey="present" name="Present" stackId="a" fill={series.present} />
                <Bar dataKey="onLeave" name="On leave" stackId="a" fill={series.leave} />
                <Bar dataKey="absent" name="Absent" stackId="a" fill={series.absent} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead title="Average hours worked" sub="Per working day" />
          <PanelBody>
            <ResponsiveContainer width="100%" height={216}>
              <LineChart data={workdayTrend} margin={{ top: 6, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={18} />
                <YAxis tickLine={false} axisLine={false} width={44} domain={[0, 'auto']} />
                <Tooltip content={<ChartTooltip formatter={(v) => fmtHours(v)} />} />
                <Line
                  type="monotone"
                  dataKey="averageHours"
                  name="Avg hours"
                  stroke={series.accent}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </PanelBody>
        </Panel>
      </Grid>

      {/* ---------------- REGISTER ---------------- */}
      <Tabs
        items={[
          { key: 'register', label: 'Daily register', count: register.length },
          { key: 'exceptions', label: 'Exceptions', count: exceptions.length },
        ]}
        value={tab}
        onChange={setTab}
      />

      <div style={{ marginTop: 14 }}>
        <DataTable
          columns={columns}
          rows={filtered}
          loading={isLoading}
          getRowId={(row) => row.employee_id}
          defaultSort={tab === 'exceptions' ? { key: 'status', dir: 'asc' } : { key: 'name', dir: 'asc' }}
          emptyIcon={tab === 'exceptions' ? Users : Clock}
          emptyTitle={
            tab === 'exceptions'
              ? 'No exceptions'
              : register.length === 0
              ? 'No register for this date'
              : 'No matching rows'
          }
          emptyText={
            tab === 'exceptions'
              ? 'Everyone clocked in on time and closed their session.'
              : register.length === 0
              ? 'No active employees, or no attendance was recorded on this date.'
              : 'Adjust the search or filters above.'
          }
          toolbar={
            <Toolbar>
              <SearchInput value={search} onChange={setSearch} placeholder="Search employee…" width={230} />
              <FilterChip
                label="Status"
                value={statusFilter}
                options={['Present', 'Late', 'On Leave', 'Absent']}
                onChange={setStatusFilter}
              />
              <FilterChip label="Department" value={department} options={departments} onChange={setDepartment} />
              {search || statusFilter || department ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSearch('');
                    setStatusFilter('');
                    setDepartment('');
                  }}
                >
                  Clear
                </Button>
              ) : null}
              <ToolbarSpacer />
              <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>
                {fmtNumber(filtered.length)} of {fmtNumber(source.length)} rows
              </span>
            </Toolbar>
          }
        />
      </div>
    </div>
  );
}
